"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Flame,
  Sparkles,
  Crown,
  Stethoscope,
  Brain,
  Target,
  Award,
  ArrowRight,
  Mic,
  BookOpen,
  CheckCircle2,
  Eye,
  Shield,
  Activity,
  Building2,
  Briefcase,
  Compass,
  Radio,
  Crosshair,
  Zap,
  Users,
  Layers,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";
import { CounterBlock } from "@/lib/blocks/counter-block";

const CRISIS_PILLARS = [
  { num: "01", title: "Diagnose Before Triaging", body: "The first pillar of crisis leadership is the discipline of forming a complete picture before allocating resources. Crisis pressures demand speed. But speed without diagnosis is the most common cause of crisis amplification. The leader who diagnoses first allocates resources to the right priorities.", icon: <Stethoscope className="size-5" />, metric: "T-0", metricLabel: "First thirty minutes", accent: "red" as const },
  { num: "02", title: "Triage With Clinical Precision", body: "Not all crises are equal. Not all priorities are aligned. The skill of ranking under fear — the same skill the physician deploys in the emergency room — determines whether the organization navigates the crisis or is consumed by it. Triage is the discipline of resource allocation under maximum constraint.", icon: <Crosshair className="size-5" />, metric: "T-30", metricLabel: "First hour", accent: "purple" as const },
  { num: "03", title: "Communicate With One Voice", body: "Crisis communication fails when the organization speaks with multiple voices. The crisis leader designates the single point of truth. The message is consistent. The cadence is predictable. The audience — internal and external — knows where to look for authoritative information. The communication architecture prevents the secondary crises that inconsistent messaging creates.", icon: <Radio className="size-5" />, metric: "T-60", metricLabel: "First two hours", accent: "cyan" as const },
  { num: "04", title: "Run the Protocol, Keep the Judgment", body: "The crisis is not the time to improvise the architecture. The protocol has been designed under calmer conditions for precisely these circumstances. Run the protocol. Keep the judgment for the moments when the protocol does not fit. The combination is the operating discipline that prevents crisis amplification.", icon: <Shield className="size-5" />, metric: "100%", metricLabel: "Protocol adherence", accent: "emerald" as const },
  { num: "05", title: "Sustain Operational Continuity", body: "The crisis is not an excuse to abandon the operations that sustain the organization. The platforms, the services, the client commitments — they continue through the crisis with sovereign-grade discipline. The ninety-nine-point-nine-nine-nine-nine percent uptime is not aspirational. It is the evidence that operational continuity survives crisis.", icon: <Activity className="size-5" />, metric: "99.9999%", metricLabel: "Uptime maintained", accent: "amber-gold" as const },
  { num: "06", title: "Regulate the Room", body: "The leader&apos;s state determines the team&apos;s capacity. In crisis, the leader&apos;s emotional regulation is the most consequential operational discipline. The composure that the leader models is the composure the team can execute. The breaking point is not the crisis. It is the leader who breaks under the crisis.", icon: <Flame className="size-5" />, metric: "Calm", metricLabel: "Under maximum pressure", accent: "coral" as const },
  { num: "07", title: "Do No Harm", body: "The interventions deployed to address the crisis must be assessed for the damage they may cause. The restructuring that fragments the team. The communication that creates panic. The decision that solves the immediate problem while creating three downstream crises. The do-no-harm discipline is the restraint that prevents crisis amplification.", icon: <Eye className="size-5" />, metric: "0", metricLabel: "Secondary crises", accent: "denim" as const },
  { num: "08", title: "Learn From Every Crisis", body: "The crisis review is the mechanism that prevents institutional repetition. Every crisis is reviewed — without blame, with diagnostic precision, and with the institutional authority to mandate changes. The system evolves with every crisis. The next crisis is faced with the architectural improvements that the previous crisis demanded.", icon: <Brain className="size-5" />, metric: "Loop", metricLabel: "Closed on every crisis", accent: "lime" as const },
];

const CRISIS_STATS = [
  { value: 100, suffix: "%", label: "Client Satisfaction", description: "Maintained through crisis", accent: "emerald" as const, icon: <TrophyIcon className="size-5" /> },
  { value: 15, suffix: "/18", label: "Campaign Victories", description: "Crisis-period performance", accent: "red" as const, icon: <Target className="size-5" /> },
  { value: 0, label: "Security Incidents", description: "Through 15+ years", accent: "cyan" as const, icon: <Shield className="size-5" /> },
  { value: 99, suffix: ".9999%", label: "Uptime", description: "Crisis-continuity verified", accent: "amber-gold" as const, icon: <Activity className="size-5" /> },
];

const FAQ_ITEMS = [
  { q: "What is \"Leading Through Crisis\" about?", a: "The signature keynote on crisis leadership — the operating discipline that produces organizational survival under maximum pressure. The argument is that crisis leadership is not a personality trait. It is a learnable, practicable, and scalable set of protocols. The evidence is a decade of sovereign-scale operations that have maintained continuity through every crisis the decade produced." },
  { q: "How is this different from other crisis leadership keynotes?", a: "Most crisis leadership keynotes focus on communication, decisiveness, and inspirational leadership. This keynote focuses on the clinical discipline that sovereign-scale operations require — the diagnostic rigor, the triage protocol, the operating continuity, and the emotional regulation that produces survival rather than amplification." },
  { q: "What are the crisis leadership pillars?", a: "Diagnose before triaging. Triage with clinical precision. Communicate with one voice. Run the protocol and keep the judgment. Sustain operational continuity. Regulate the room. Do no harm. Learn from every crisis. Together, these eight pillars form the architecture of crisis leadership." },
  { q: "How long is the standard keynote?", a: "Forty-five to sixty minutes for the standard keynote. Fireside chat runs thirty to forty-five minutes. Workshop runs two to three hours. Executive briefing runs sixty to ninety minutes. All formats are available virtually and in person." },
  { q: "What audiences benefit most from this keynote?", a: "Executive leadership teams facing acute or chronic crisis, government and defense leaders navigating geopolitical volatility, healthcare systems under surge conditions, technology organizations managing security incidents, and any leader who has been told that crisis demands inspiration when crisis demands discipline." },
  { q: "How does this keynote connect to other topics in the portfolio?", a: "Crisis leadership is the application of the clinical method — diagnose, triage, protocol, outcome — under maximum pressure. The connections to Leadership as Diagnosis, How Excellence Actually Runs, and The Triple Threat are structural. The audience can book a series that provides the complete crisis-capable operating framework." },
];

function TrophyIcon({ className }: { className?: string }) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></svg>;
}

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const THREE_PILLARS = [
  {
    num: "01",
    icon: Eye,
    title: "Pillar One — Perception Containment",
    body: "Every crisis becomes a perception crisis within minutes of its first public mention. Pillar One contains the perception crisis by deploying counter-narratives across distribution points simultaneously, neutralizing negative content at source, and restoring the narrative across all platforms in real time.",
    accent: "red",
  },
  {
    num: "02",
    icon: Crosshair,
    title: "Pillar Two — Threat Vector Identification",
    body: "Crisis response with intelligence is predictive. The second pillar addresses the crisis as an intelligence event — identifying the threat vector, the source, the motive, the amplification mechanism, and the escalation trajectory before the adversary designs the next move.",
    accent: "amber-gold",
  },
  {
    num: "03",
    icon: Shield,
    title: "Pillar Three — Communications Security",
    body: "Crises are not only reputation events — they are security events. Adversaries exploit the chaos of crisis to escalate attacks. The third pillar ensures the organization&apos;s crisis response operates within a secure communications architecture that adversaries cannot penetrate.",
    accent: "cyan",
  },
];

const EVIDENCE = [
  { metric: "384–1,416", suffix: "x", label: "Faster Response", sub: "vs. conventional approaches", accent: "red" },
  { metric: "85–95", suffix: "%", label: "Success Rate", sub: "Crisis transformation", accent: "amber-gold" },
  { metric: "72", suffix: "h", label: "Advance Warning", sub: "Before competitors identify threats", accent: "cyan" },
  { metric: "500", suffix: "+", label: "Pre-Built Playbooks", sub: "Scenario-specific protocols", accent: "emerald" },
];

const TAKEAWAYS = [
  { title: "Build the architecture before the crisis arrives.", body: "The single most important crisis leadership insight is the most overlooked: the crisis response architecture must be built before the crisis, not during. Assess current crisis readiness — detection, response, communication, recovery.", accent: "red" },
  { title: "The golden hour is an architecture, not a deadline.", body: "The first six hours determine the trajectory of the crisis. Build the golden hour architecture — automated detection, pre-built playbooks, coordinated response that activate within minutes.", accent: "amber-gold" },
  { title: "Crisis response must operate across all dimensions simultaneously.", body: "The three-pillar architecture — perception, intelligence, security — activates simultaneously because the crisis operates across all dimensions simultaneously. Sequential crisis response is failed crisis response.", accent: "cyan" },
  { title: "Design the narrative before the adversary designs it.", body: "The narrative trajectory of the crisis is determined in the first hours. Establish the authoritative narrative early — the verified, documented response deployed before adversaries and the rumor mill.", accent: "emerald" },
  { title: "Measure crisis response by outcomes, not activities.", body: "The only metric that determines whether the crisis response architecture is functioning is the outcome it produces. Establish outcome metrics — stakeholder confidence retention, narrative control, timeline to stabilization.", accent: "purple" },
];

const AUDIENCE = [
  { icon: Crown, title: "C-Suite Executives & Boards", body: "Decision-makers who recognize the organization&apos;s crisis capability is inadequate — who understand the crisis plan in the drawer will not produce outcomes when the crisis arrives.", accent: "red" },
  { icon: Building2, title: "Government & Sovereign", body: "Decision-makers operating at the level where crises carry national consequences. The three-pillar architecture directly relevant to sovereign-level operations.", accent: "amber-gold" },
  { icon: Activity, title: "Crisis Management Professionals", body: "Professionals responsible for organizational crisis preparedness, business continuity planning, and incident response. The architecture crisis management theory aspires to but rarely achieves.", accent: "cyan" },
  { icon: Shield, title: "Security & Intelligence Leaders", body: "Chief information security officers, intelligence directors, and security operations leaders. The Security Operations Layer communications security dimension directly relevant.", accent: "emerald" },
  { icon: Briefcase, title: "Conference Organizers", body: "Events focused on resilience, risk management, crisis leadership, organizational security, or business continuity seeking a centerpiece that differentiates.", accent: "purple" },
  { icon: Compass, title: "Fortune 500 & Global Enterprises", body: "Organizations operating at scale — where a single crisis can destroy decades of brand equity, compromise millions of customer records, or trigger regulatory action across multiple jurisdictions.", accent: "denim" },
];

const FORMATS = [
  { label: "Standard Keynote", duration: "45–60 min", body: "Structured presentation from core argument through the Crisis Response Engine architecture and the golden hour principle to the three-pillar response system and actionable takeaways.", accent: "red" },
  { label: "Fireside Chat", duration: "30–45 min", body: "Conversational exploration of crisis leadership themes, guided by a moderator. Effective for executive audiences who prefer dialogue over monologue.", accent: "amber-gold" },
  { label: "Workshop / Masterclass", duration: "2–4 hrs", body: "Designed for organizations building their crisis response architecture. Tangible output: draft crisis response playbooks, communication frameworks, recovery protocols.", accent: "cyan" },
  { label: "Executive Briefing", duration: "60–90 min", body: "Private session applying the three-pillar framework as a diagnostic — identifying which components the organization has built, which are missing.", accent: "emerald" },
  { label: "Panel Contribution", duration: "20–30 min", body: "Focused contribution to multi-speaker discussions on crisis leadership, organizational resilience, or business continuity.", accent: "purple" },
  { label: "Virtual Delivery", duration: "All formats", body: "All formats available for virtual events with the same operational authority and engagement quality.", accent: "denim" },
];

const GAINS = [
  "A crisis response architecture, not a crisis plan — the three-pillar framework deployable immediately",
  "The clinical parallel — the medical discipline applied to crisis leadership with clinical precision and verified evidence",
  "Sovereign-scale evidence — drawn from a crisis response architecture that has produced 85 to 95 percent success across sovereign engagements",
  "The golden hour framework — the principle that 95 percent of crisis damage occurs in the first six hours",
  "The three-pillar architecture — perception containment, threat intelligence, communications security operating simultaneously",
  "Five actionable insights — build the architecture before the crisis, design the narrative, measure by outcomes",
];

export default function LeadingThroughCrisisPage() {
  return (
    <>
      <PageHero
        icon={Flame}
        overline="Additional Keynote"
        overlineClassName="text-red"
        title={
          <>
            Crisis Is Inevitable. <span className="text-gradient-premium">Transformation Is Optional.</span>
          </>
        }
        subtitle="Leading Through Crisis is the signature keynote on the crisis response methodology that operates at 384 to 1,416 times the speed of conventional approaches, with an 85 to 95 percent success rate. Not a speech about crisis avoidance — a blueprint for crisis transformation. The architecture that turns existential threats into competitive advantage."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Speaking", href: "/speaking/" }, { label: "Leading Through Crisis" }]}
        badge="The Crisis Response Engine methodology"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Sparkles className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Abstract</span>
          </div>
          <h2 className="text-display-md mb-5">The crisis response architecture that operates at sovereign speed.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Every organization will face a crisis. The question is not whether it will arrive, but whether the organization has built the operating infrastructure to survive it, transform through it, and emerge stronger on the other side. The keynote presents the crisis response architecture tested under conditions where the margin for error was zero and the consequences of failure were national.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Three pillars activate simultaneously: perception containment, threat vector identification, and communications security. The architecture is not theoretical. It is the operational reality that has protected the most sensitive entities on earth from crises that would have destroyed organizations operating without this infrastructure. The audience leaves with a framework that can be deployed immediately — not a theory to be debated, but a crisis response architecture to be built before the crisis arrives.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Zap className="size-3.5 text-red" />
            <span className="text-overline text-red">The Evidence</span>
          </div>
          <h2 className="text-display-md mb-4">Sovereign-scale crisis response.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The crisis transformation engine. 384 to 1,416 times faster than conventional approaches. 85 to 95 percent success rate. The metrics that define sovereign-grade crisis response.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {EVIDENCE.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-2`}>
                  {s.metric}{s.suffix}
                </div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{s.label}</div>
                <div className="text-caption text-muted-foreground">{s.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.amber_gold?.pill || ACCENT["amber-gold"].pill} mb-4`}>
            <Layers className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Three-Pillar Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">Perception, intelligence, and security — activating simultaneously.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Three operational dimensions that activate simultaneously when the crisis arrives. Each addresses a critical dimension of the threat. Each reinforces the others in a closed-loop system.
          </p>
        </div>
        <div className="space-y-4 max-w-4xl mx-auto">
          {THREE_PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-6 items-center">
                  <div className={`size-16 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <p.icon className="size-7 text-white" />
                  </div>
                  <div>
                    <span className={`text-overline ${a.text}`}>Pillar {p.num}</span>
                    <h3 className={`text-display-sm mb-2 ${a.text}`}>{p.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Stethoscope className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">The Clinical Parallel</span>
            </div>
            <h2 className="text-display-md mb-4">The golden hour — the same discipline that keeps patients alive.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              In medicine, the golden hour is the first sixty minutes after traumatic injury — the window during which the medical intervention determines whether the patient survives. In crisis leadership, the golden hour extends to six hours — the window during which the crisis response determines whether the organization survives intact, survives with damage, or does not survive at all.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              95 percent of crisis damage occurs within the first six hours. The first six hours determine the trajectory of the crisis: whether it remains containable or escalates beyond control, whether stakeholder confidence holds or collapses, whether the narrative remains manageable or is seized by adversaries.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The architecture that produces outcomes within the golden hour is the architecture that transforms crisis from existential threat to manageable event. The organizations that master the golden hour have built the systems that produce mastery. The organizations that fail the golden hour have not built those systems. The difference is design, not luck.
            </p>
          </div>
          <div className="space-y-3">
            {[
              { phase: "Hour One — Detection & Assessment", body: "Identifying the crisis, classifying severity, activating the response architecture. The hour the trajectory is determined.", accent: "red" },
              { phase: "Hour Two — Containment", body: "Deploying initial countermeasures to prevent escalation, securing communications, establishing the narrative.", accent: "amber-gold" },
              { phase: "Hours Three–Four — Mobilization", body: "Activating the full response architecture across all dimensions, coordinating the multi-platform response, deploying the counter-narrative.", accent: "cyan" },
              { phase: "Hours Five–Six — Stabilization", body: "Establishing stakeholder confidence, consolidating containment, transitioning from response to recovery.", accent: "emerald" },
            ].map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.phase}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                      <span className="text-caption font-bold text-white">{i + 1}</span>
                    </div>
                    <div>
                      <h3 className={`text-heading-sm mb-1 ${a.text}`}>{p.phase}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <CheckCircle2 className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Key Takeaways</span>
          </div>
          <h2 className="text-display-md mb-4">Five insights the audience implements immediately.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The minimum viable crisis response architecture. Specific, implementable actions that transform organizational crisis capability starting the next business day.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3 max-w-5xl mx-auto">
          {TAKEAWAYS.map((t, i) => {
            const accents = ["red", "amber-gold", "cyan", "emerald", "purple"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-9 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <span className="text-caption font-bold text-white">{i + 1}</span>
                  </div>
                  <div>
                    <h3 className={`text-heading-sm mb-1 ${a.text}`}>{t.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Users className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Who This Keynote Serves</span>
          </div>
          <h2 className="text-display-md mb-4">Ideal audience.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The keynote serves decision-makers who recognize the gap between crisis planning and crisis outcomes — and who need the architectural solution rather than another theoretical framework.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCE.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <a.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-sm mb-1.5 ${ac.text}`} dangerouslySetInnerHTML={{ __html: a.title }} />
                    <p className="text-caption text-muted-foreground leading-relaxed">{a.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-5`}>
              <Briefcase className="size-3.5 text-cyan" />
              <span className="text-overline text-cyan">Format Options</span>
            </div>
            <h2 className="text-display-md mb-4">Flexibility for every event configuration.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Six delivery formats, each calibrated to the audience&apos;s domain, their specific threat landscape, and the outcomes they seek. The customization is structural, with industries that benefit from specific customization including government, financial services, technology, healthcare, defense, and energy.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FORMATS.map((f, i) => {
              const a = ACCENT[f.accent];
              return (
                <motion.div
                  key={f.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className={`text-heading-sm ${a.text}`}>{f.label}</h3>
                    <span className={`text-caption font-bold ${a.text}`}>{f.duration}</span>
                  </div>
                  <p className="text-caption text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: f.body }} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Flame className="size-10 text-red mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Book this keynote.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Leading Through Crisis is the crisis transformation keynote in the portfolio. 384 to 1,416 times faster response. 85 to 95 percent success rate. The architecture that turns existential threats into competitive advantage.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Book the Keynote
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/speaking/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <BookOpen className="size-4" />
                All Speaking Topics
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-5`}>
            <Flame className="size-3.5 text-red" />
            <span className="text-overline text-red">Signature Architecture</span>
          </div>
          <h2 className="text-display-md mb-4">The Crisis Leadership Pillars.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Eight pillars of crisis leadership — each drawn from clinical practice, each proven in sovereign-scale operations, each immediately applicable to the crises the audience faces. The pillars are not theoretical. They are the operating architecture that has maintained continuity through every crisis the past decade produced.
          </p>
        </div>
        <PillarGridBlock pillars={CRISIS_PILLARS} columns={4} variant="numbered" />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Shield className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Sovereign-Scale Continuity</span>
          </div>
          <h2 className="text-display-md mb-4">Outcomes maintained through crisis.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The crisis leadership framework is not aspirational. The outcomes that demonstrate its efficacy are measured, structural, and sustained across the most demanding conditions the operating model has faced.
          </p>
        </div>
        <CounterBlock stats={CRISIS_STATS} columns={4} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
            <Target className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Problem This Keynote Solves</span>
          </div>
          <h2 className="text-display-md mb-4">The leadership void that crisis exposes.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Every crisis exposes a leadership void. The leader who has not built the discipline before the crisis cannot improvise the discipline during the crisis. The organization that has not codified the protocol before the crisis cannot execute the protocol under the pressure of the crisis. The void is structural — and it is the void that the keynote fills.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The conventional approach to crisis leadership is reactive. Organizations develop crisis plans after the previous crisis. They hire crisis consultants when the next crisis arrives. They communicate when the cameras are rolling. The reactive approach produces organizations that are perpetually surprised by crises they could have anticipated and perpetually underprepared for the crises they are facing.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The keynote solves this by presenting the proactive architecture. Crisis leadership is not a response. It is a discipline — built before the crisis, practiced under pressure, and refined after every crisis through the institutional learning mechanism. The eight pillars are the architecture. The sovereign-scale outcomes are the evidence. The discipline is learnable, practicable, and immediately applicable to the crises the audience faces.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <AccordionBlock
          items={FAQ_ITEMS}
          eyebrow="Frequently Asked Questions"
          title="What decision-makers ask before booking."
          description="The questions that surface most often in preliminary conversations. Each answer reflects the operating reality, not the marketing narrative."
        />
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "How Excellence Actually Runs", description: "The foundational system — protocols, checklists, reviews, accountability — that prevents many crises from materializing.", href: "/speaking/how-excellence-actually-runs/", icon: Award, accent: "red" },
          { title: "Leadership as Diagnosis", description: "The clinical skill set that crisis leaders require — diagnosis, triage, protocol, outcome accountability under maximum pressure.", href: "/speaking/leadership-as-diagnosis/", icon: Brain, accent: "purple" },
          { title: "The 70% Project", description: "The talent architecture — majority-female teams that outperform on the dimensions crisis response demands.", href: "/speaking/the-70-percent-project/", icon: CheckCircle2, accent: "emerald" },
          { title: "The Triple Threat", description: "The cross-domain integration that crisis response demands — clinical precision, technological scale, political-grade stakes.", href: "/speaking/the-triple-threat/", icon: Compass, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
